import React, { useMemo } from 'react';
import './Dashboard.css';

const SENSOR_META = {
  sensorAReading: { label: 'Temperature', unit: '°C',  icon: '🌡️', warn: 90,  crit: 100 },
  sensorBReading: { label: 'Pressure',    unit: ' PSI', icon: '🔵', warn: 60,  crit: 70  },
  sensorCReading: { label: 'Vibration',   unit: ' Hz',  icon: '📳', warn: 120, crit: 140 },
  sensorDReading: { label: 'Speed',       unit: ' RPM', icon: '⚡', warn: 75,  crit: 85  },
};

const getStatus = (value, warn, crit) => {
  if (value >= crit) return 'critical';
  if (value >= warn) return 'warning';
  return 'healthy';
};

// Progress bar
const ProgressBar = ({ value, warn, crit, max }) => {
  const pct = Math.min((value / max) * 100, 100);
  const status = getStatus(value, warn, crit);
  return (
    <div className="progress-track">
      <div
        className={`progress-fill ${status}`}
        style={{ width: `${pct}%` }}
      />
      {warn && <div className="progress-marker warn" style={{ left: `${(warn / max) * 100}%` }} />}
      {crit && <div className="progress-marker crit" style={{ left: `${(crit / max) * 100}%` }} />}
    </div>
  );
};

const Dashboard = ({ telemetryData = [], graphData = null }) => {
  const sensorStats = useMemo(() => {
    if (telemetryData.length === 0) return {};
    const result = {};
    Object.keys(SENSOR_META).forEach(key => {
      const vals = telemetryData.map(d => d[key] || 0);
      result[key] = {
        avg: vals.reduce((a, b) => a + b, 0) / vals.length,
        min: Math.min(...vals),
        max: Math.max(...vals),
      };
    });
    return result;
  }, [telemetryData]);

  const healthCounts = useMemo(() => {
    let healthy = 0, warning = 0, critical = 0;
    telemetryData.forEach(d => {
      const isCrit = Object.entries(SENSOR_META).some(
        ([k, m]) => (d[k] || 0) >= m.crit
      );
      const isWarn = Object.entries(SENSOR_META).some(
        ([k, m]) => (d[k] || 0) >= m.warn
      );
      if (isCrit) critical++;
      else if (isWarn) warning++;
      else healthy++;
    });
    return { healthy, warning, critical, total: telemetryData.length };
  }, [telemetryData]);

  const topAlerts = useMemo(() => {
    const alerts = [];
    telemetryData.forEach(d => {
      Object.entries(SENSOR_META).forEach(([key, meta]) => {
        const val = d[key] || 0;
        const status = getStatus(val, meta.warn, meta.crit);
        if (status !== 'healthy') {
          alerts.push({
            componentID: d.componentID,
            sensor: meta.label,
            value: val,
            unit: meta.unit,
            status,
          });
        }
      });
    });
    return alerts.sort((a, b) => (b.status === 'critical' ? 1 : -1)).slice(0, 6);
  }, [telemetryData]);

  const nodeCount = graphData?.nodes?.length || 0;
  const edgeCount = graphData?.edges?.length || 0;
  const lineCount = graphData?.nodes?.filter(n => n.type === 'line').length || 0;
  const machineCount = graphData?.nodes?.filter(n => n.type === 'machine').length || 0;
  const componentCount = graphData?.nodes?.filter(n => n.type === 'component').length || 0;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Manufacturing Overview</h2>
        <div className="dashboard-subtitle">Real-time Digital Twin status</div>
      </div>

      {/* Top stat cards */}
      <div className="stat-cards-row">
        <div className="stat-card-wide">
          <div className="stat-icon">🏭</div>
          <div className="stat-body">
            <div className="stat-big">{nodeCount}</div>
            <div className="stat-label">Total Nodes</div>
          </div>
          <div className="stat-breakdown">
            <span>{lineCount} lines</span>
            <span>{machineCount} machines</span>
            <span>{componentCount} components</span>
          </div>
        </div>

        <div className="stat-card-wide">
          <div className="stat-icon">🔗</div>
          <div className="stat-body">
            <div className="stat-big">{edgeCount}</div>
            <div className="stat-label">Relationships</div>
          </div>
        </div>

        <div className={`stat-card-wide ${healthCounts.critical > 0 ? 'critical' : healthCounts.warning > 0 ? 'warning' : 'healthy'}`}>
          <div className="stat-icon">
            {healthCounts.critical > 0 ? '🔴' : healthCounts.warning > 0 ? '🟡' : '🟢'}
          </div>
          <div className="stat-body">
            <div className="stat-big">{healthCounts.healthy}</div>
            <div className="stat-label">Healthy Components</div>
          </div>
          <div className="stat-breakdown">
            <span className="warn-text">{healthCounts.warning} warn</span>
            <span className="crit-text">{healthCounts.critical} critical</span>
          </div>
        </div>

        <div className="stat-card-wide">
          <div className="stat-icon">📡</div>
          <div className="stat-body">
            <div className="stat-big">{telemetryData.length}</div>
            <div className="stat-label">Active Sensors</div>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Sensor averages */}
        <div className="dash-card">
          <div className="dash-card-title">Sensor Averages</div>
          <div className="sensor-avg-list">
            {Object.entries(SENSOR_META).map(([key, meta]) => {
              const s = sensorStats[key];
              if (!s) return null;
              const status = getStatus(s.avg, meta.warn, meta.crit);
              const maxVal = meta.crit * 1.2;
              return (
                <div key={key} className="sensor-avg-item">
                  <div className="sensor-avg-top">
                    <span className="sensor-avg-icon">{meta.icon}</span>
                    <span className="sensor-avg-label">{meta.label}</span>
                    <span className={`sensor-avg-value ${status}`}>
                      {s.avg.toFixed(1)}{meta.unit}
                    </span>
                  </div>
                  <ProgressBar value={s.avg} warn={meta.warn} crit={meta.crit} max={maxVal} />
                  <div className="sensor-avg-range">
                    <span>min {s.min.toFixed(1)}</span>
                    <span>max {s.max.toFixed(1)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Health ring */}
        <div className="dash-card">
          <div className="dash-card-title">Health Distribution</div>
          <HealthRing counts={healthCounts} />
          <div className="health-legend">
            <div className="health-legend-item healthy">
              <span className="h-dot" />
              <span>Healthy</span>
              <span className="h-count">{healthCounts.healthy}</span>
            </div>
            <div className="health-legend-item warning">
              <span className="h-dot" />
              <span>Warning</span>
              <span className="h-count">{healthCounts.warning}</span>
            </div>
            <div className="health-legend-item critical">
              <span className="h-dot" />
              <span>Critical</span>
              <span className="h-count">{healthCounts.critical}</span>
            </div>
          </div>
        </div>

        {/* Active alerts */}
        <div className="dash-card wide">
          <div className="dash-card-title">Active Alerts</div>
          {topAlerts.length === 0 ? (
            <div className="dash-all-clear">
              <span>✅</span>
              <span>All systems nominal — no active alerts</span>
            </div>
          ) : (
            <div className="alerts-list">
              {topAlerts.map((alert, i) => (
                <div key={i} className={`alert-item ${alert.status}`}>
                  <span className="alert-status-dot" />
                  <span className="alert-comp">#{alert.componentID}</span>
                  <span className="alert-sensor">{alert.sensor}</span>
                  <span className="alert-value">{alert.value.toFixed(1)}{alert.unit}</span>
                  <span className={`alert-badge ${alert.status}`}>
                    {alert.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="dash-card">
          <div className="dash-card-title">Quick Info</div>
          <div className="quick-info-list">
            <div className="quick-info-item">
              <span className="qi-label">Product</span>
              <span className="qi-value">Ball Bearing Factory</span>
            </div>
            <div className="quick-info-item">
              <span className="qi-label">Data Model</span>
              <span className="qi-value">RDF/OWL Ontology</span>
            </div>
            <div className="quick-info-item">
              <span className="qi-label">Graph DB</span>
              <span className="qi-value">Amazon Neptune</span>
            </div>
            <div className="quick-info-item">
              <span className="qi-label">Telemetry Rate</span>
              <span className="qi-value">30s refresh</span>
            </div>
            <div className="quick-info-item">
              <span className="qi-label">Visualization</span>
              <span className="qi-value">Cytoscape.js</span>
            </div>
            <div className="quick-info-item">
              <span className="qi-label">AI Assistant</span>
              <span className="qi-value">Claude Opus 4.6</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Donut ring chart using SVG
const HealthRing = ({ counts }) => {
  const total = counts.total || 1;
  const slices = [
    { value: counts.healthy,  color: '#48bb78' },
    { value: counts.warning,  color: '#f6ad55' },
    { value: counts.critical, color: '#fc8181' },
  ];

  const R = 54; const cx = 70; const cy = 70;
  const circumference = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="health-ring-wrapper">
      <svg viewBox="0 0 140 140" className="health-ring-svg">
        {/* Background circle */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#1a2332" strokeWidth="18" />
        {slices.map((slice, i) => {
          const dashLen = (slice.value / total) * circumference;
          const gap = circumference - dashLen;
          const el = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={R}
              fill="none"
              stroke={slice.color}
              strokeWidth="18"
              strokeDasharray={`${dashLen} ${gap}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cy})`}
              opacity={slice.value === 0 ? 0 : 1}
            />
          );
          offset += dashLen;
          return el;
        })}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="#e2e8f0" fontSize="22" fontWeight="700">
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#718096" fontSize="10">
          components
        </text>
      </svg>
    </div>
  );
};

export default Dashboard;
